import {effect, Injectable, signal} from '@angular/core';
import {catchError, Observable, of, tap} from 'rxjs';
import {AuthService} from './auth-service';
import {OfferingCreateInput} from '../models/api/crm/OfferingCreateInput';
import {CrmDocumentSummary} from '../models/api/crm/CrmDocumentSummary';
import {OfferingDetails} from '../models/api/crm/OfferingDetails';
import {OfferingSummary} from '../models/api/crm/OfferingSummary';
import {I18nService} from './i18n-service';
import {OfferingApiService} from './offering-api-service';
import {OfferingUpdateInput} from '../models/api/crm/OfferingUpdateInput';

@Injectable({
  providedIn: 'root'
})
export class OfferingService {
  private readonly _offerings = signal<OfferingSummary[]>([]);
  readonly offerings = this._offerings.asReadonly();

  private readonly _offeringsLoading = signal<boolean>(false);
  readonly offeringsLoading = this._offeringsLoading.asReadonly();

  private readonly _offeringError = signal<string | null>(null);
  readonly offeringError = this._offeringError.asReadonly();

  constructor(
    private readonly offeringApiService: OfferingApiService,
    private readonly authService: AuthService,
    private readonly i18n: I18nService
  ) {
    effect(() => {
      if (!this.authService.shouldShowProtectedContent()) {
        this._offerings.set([]);
        this._offeringError.set(null);
        this._offeringsLoading.set(false);
        return;
      }

      if (!this.authService.isAuthenticated()) {
        return;
      }

      this.getOfferings();
    });
  }

  public getOfferings(): void {
    this._offeringsLoading.set(true);
    this._offeringError.set(null);

    this.offeringApiService.getOfferings()
      .pipe(
        catchError(() => {
          this._offeringsLoading.set(false);
          this._offeringError.set(this.i18n.t('errors.offerings.load'));
          return of([]);
        })
      )
      .subscribe(value => {
        this._offerings.set(value);
        this._offeringsLoading.set(false);
      });
  }

  public createOffering(payload: OfferingCreateInput): Observable<OfferingDetails> {
    return this.offeringApiService.createOffering(payload)
      .pipe(
        tap(() => this.getOfferings())
      );
  }

  public getOffering(offeringId: string): Observable<OfferingDetails> {
    return this.offeringApiService.getOffering(offeringId);
  }

  public updateOffering(offeringId: string, payload: OfferingUpdateInput): Observable<OfferingDetails> {
    return this.offeringApiService.updateOffering(offeringId, payload)
      .pipe(
        tap(() => this.getOfferings())
      );
  }

  public deleteOffering(offeringId: string): Observable<void> {
    return this.offeringApiService.deleteOffering(offeringId)
      .pipe(
        tap(() => this.getOfferings())
      );
  }

  public getOfferingDocuments(offeringId: string): Observable<CrmDocumentSummary[]> {
    return this.offeringApiService.getOfferingDocuments(offeringId);
  }

  public uploadOfferingDocument(offeringId: string, file: File): Observable<CrmDocumentSummary> {
    return this.offeringApiService.uploadOfferingDocument(offeringId, file);
  }

  public deleteOfferingDocument(offeringId: string, documentId: string): Observable<void> {
    return this.offeringApiService.deleteOfferingDocument(offeringId, documentId);
  }

  public downloadOfferingDocument(offeringId: string, documentId: string): Observable<Blob> {
    return this.offeringApiService.downloadOfferingDocument(offeringId, documentId);
  }

  public clearOfferingError(): void {
    this._offeringError.set(null);
  }
}
